import { DOCUMENT_STYLES, MSO_DEFINITIONS } from '../../../constants';
import { convertDocxToHTML } from '../../../helpers/convertFileType';
import { StyleValidationResult } from '../../../pages/CatalogTemplateForm/types';

const PLACEHOLDER_REGEX = /(<<\s*[^<>]+\s*>>|{{\s*[^{}]+\s*}})/g;
const htmlConversionCache = new Map<string, Promise<{ html: string; messages: string[] }>>();

const parseQueryParams = (queryParams: string) => {
  const params: Record<string, string> = {};
  if (!queryParams) return params;

  queryParams.split('&').forEach((param) => {
    const [key, value] = param.split('=');
    if (key && value) {
      params[key] = value;
    }
  });

  return params;
};

const getDocumentFileFromFormData = (data: FormData) => {
  const documentFile = data.get('documentFile');
  if (!(documentFile instanceof File)) {
    throw new Error('documentFile is required');
  }
  return documentFile;
};

const getCacheKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

const getConvertedData = async (file: File) => {
  const key = getCacheKey(file);

  if (!htmlConversionCache.has(key)) {
    htmlConversionCache.set(
      key,
      convertDocxToHTML(file).then((converted) => ({
        html: converted.value || '',
        messages: (converted.messages || [])
          .map((message) => message.message || '')
          .filter(Boolean),
      })),
    );
  }

  return htmlConversionCache.get(key) as Promise<{ html: string; messages: string[] }>;
};

const getStyleGuideFileFromFormData = (data: FormData) => {
  const styleGuideFile = data.get('styleGuideFile');
  if (!(styleGuideFile instanceof File)) {
    return undefined;
  }
  return styleGuideFile;
};

const buildValidationIssue = (
  issue: string,
  actual: string,
  expected = 'N/A',
  text = 'N/A',
): StyleValidationResult => ({
  elementType: 'document',
  elementStyles: [],
  issue,
  expected,
  actual,
  text,
  location: 'Document',
});

const getPlaceholdersFromHtml = (html: string) => {
  const matches = html.match(PLACEHOLDER_REGEX) || [];
  const uniqueValues = Array.from(new Set(matches.map((item) => item.trim())));

  const projectPlaceholders = uniqueValues.filter((value) => /project|\[project\]/i.test(value));
  const templateVariables = uniqueValues.filter((value) => !projectPlaceholders.includes(value));

  return {
    templateVariables,
    projectPlaceholders,
  };
};

const buildWordBlobFromHtml = (html: string) => {
  const preHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><style type='text/css'>${
    DOCUMENT_STYLES + MSO_DEFINITIONS
  }</style><title>Export HTML To Doc</title></head><body>`;
  const postHtml = '</body></html>';

  return new Blob(['\ufeff', `${preHtml}${html}${postHtml}`], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

export const validateDocumentApi = async (data: FormData) => {
  const documentFile = getDocumentFileFromFormData(data);
  const styleGuideFile = getStyleGuideFileFromFormData(data);
  const issues: StyleValidationResult[] = [];

  if (!documentFile.name.toLowerCase().endsWith('.docx')) {
    issues.push(
      buildValidationIssue('Unsupported document format', documentFile.name, 'A .docx file'),
    );
    return { data: issues };
  }

  if (styleGuideFile) {
    try {
      const styleGuideContent = await styleGuideFile.text();
      JSON.parse(styleGuideContent);
    } catch (_error) {
      issues.push(
        buildValidationIssue(
          'Invalid style guide JSON',
          styleGuideFile.name,
          'Valid JSON style guide',
        ),
      );
    }
  }

  const convertedData = await getConvertedData(documentFile);
  convertedData.messages.forEach((message) => {
    issues.push(buildValidationIssue('DOCX conversion warning', message, 'No conversion warnings'));
  });

  if (!convertedData.html || convertedData.html.trim() === '') {
    issues.push(buildValidationIssue('Empty converted HTML', 'No content extracted'));
  }

  return { data: issues };
};

export const exportDocumentApi = async (data: FormData) => {
  const htmlFile = data.get('htmlFile');
  if (!(htmlFile instanceof File)) {
    throw new Error('htmlFile is required');
  }

  const html = await htmlFile.text();
  const blob = buildWordBlobFromHtml(html);

  return { data: blob };
};

export const templateAndProjectFieldsApi = async (data: FormData, queryParams: string) => {
  const documentFile = getDocumentFileFromFormData(data);
  const params = parseQueryParams(queryParams);

  const { html } = await getConvertedData(documentFile);
  const { templateVariables, projectPlaceholders } = getPlaceholdersFromHtml(html);

  if (params.type === 'template_variable') {
    return { data: templateVariables };
  }

  if (params.type === 'project_field') {
    return { data: projectPlaceholders };
  }

  return {
    data: {
      templateVariables,
      projectPlaceholders,
    },
  };
};
